import '../styles/components/MealCard.css';
import { useState } from 'react';
import { Spinner } from 'react-bootstrap';

export type Ingredient = { id: number; name: string };

export type MealCardPlan = {
  date: string;
  typeId: number;
  mealId: number | null; // null = AI-suggested new meal
  mealName?: string;
  mealVideoUrl?: string;
  mealImageUrl?: string;
  mealIngredients?: Ingredient[];
  isOwnMeal?: boolean;
  // AI fields
  isAiSuggestion?: boolean; // true = new meal not in library (Option C card)
  reason?: string; // AI's reason for picking this meal
  suggestionIngredients?: string[]; // ingredient names for new AI meals
  isSkeleton?: boolean; // true = placeholder during streaming
};

const PLACEHOLDER_IMG =
  'https://thetac.tech/wp-content/uploads/2024/09/placeholder-288.png';

interface MealCardProps {
  plan: MealCardPlan;
  typeLabel: string;
  typeIcon: string;
  isShuffling?: boolean;
  isRevealing?: boolean;
  isFlipped?: boolean;
  onFlip?: () => void;
  onShuffle?: () => void;
  onVideo?: (url: string) => void;
  onSaveToLibrary?: () => void;
  /** compact mode for week plan cards */
  compact?: boolean;
  /** highlights AI suggestion cards that need an action before saving */
  isAttention?: boolean;
}

export default function MealCard({
  plan,
  typeLabel,
  typeIcon,
  isShuffling,
  isRevealing,
  isFlipped,
  onFlip,
  onShuffle,
  onVideo,
  onSaveToLibrary,
  compact,
  isAttention,
}: MealCardProps) {
  const [saving, setSaving] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  // ── Skeleton variant ──────────────────────────────
  if (plan.isSkeleton) {
    return (
      <div
        className={`mc-card mc-card-skeleton${compact ? ' mc-card-compact' : ''}`}
      >
        <div className='mc-label'>
          <span className='mc-label-icon'>{typeIcon}</span>
          {typeLabel}
        </div>
        <div className='mc-skeleton-image' />
        <div className='mc-body'>
          <div className='mc-skeleton-line' />
          <div className='mc-skeleton-line mc-skeleton-line-short' />
        </div>
      </div>
    );
  }

  // ── AI Suggestion variant (new meal, not in library) ──
  if (plan.isAiSuggestion) {
    return (
      <div
        className={`mc-card mc-card-ai-suggestion${compact ? ' mc-card-compact' : ''}${isAttention ? ' mc-card-attention' : ''}`}
      >
        <div className='mc-label'>
          <span className='mc-label-icon'>{typeIcon}</span>
          {typeLabel}
        </div>
        {plan.mealImageUrl ? (
          <div className='mc-img-wrapper'>
            {!imgLoaded && <div className='mc-img-placeholder' />}
            <img
              className='mc-image'
              src={plan.mealImageUrl}
              alt={plan.mealName}
              loading='lazy'
              decoding='async'
              onLoad={() => setImgLoaded(true)}
            />
            <span className='mc-ai-badge'>✨ AI Generated</span>
          </div>
        ) : (
          <div className='mc-ai-image-area'>
            🤖
            <span className='mc-ai-badge'>✨ AI Generated</span>
          </div>
        )}
        <div className='mc-body'>
          <div className='mc-name'>{plan.mealName}</div>
          {plan.reason && <div className='mc-reason'>"{plan.reason}"</div>}
          {plan.suggestionIngredients &&
            plan.suggestionIngredients.length > 0 && (
              <div className='mc-suggestion-ingredients'>
                {plan.suggestionIngredients.slice(0, 4).map((ing) => (
                  <span key={ing} className='mc-suggestion-ingredient'>
                    {ing}
                  </span>
                ))}
              </div>
            )}
          {isAttention && (
            <div className='mc-attention-chip'>
              <i className='fa-solid fa-circle-exclamation' />
              Save to library or swap meal to include in plan
            </div>
          )}
          <div className='mc-actions'>
            <a
              className='mc-btn mc-btn-video'
              href={`https://www.youtube.com/results?search_query=how+to+cook+${encodeURIComponent(plan.mealName ?? '')}`}
              target='_blank'
              rel='noreferrer'
            >
              <i className='fa-solid fa-play'></i> Recipe
            </a>
            {onShuffle && (
              <button
                className='mc-btn mc-btn-shuffle'
                disabled={isShuffling}
                onClick={onShuffle}
              >
                {isShuffling ? (
                  <Spinner animation='border' size='sm' />
                ) : (
                  <><i className='fa-solid fa-wand-magic-sparkles'></i> Regenerate</>
                )}
              </button>
            )}
            <button
              className='mc-btn-save-to-library'
              disabled={!onSaveToLibrary || saving}
              onClick={async () => {
                if (!onSaveToLibrary) return;
                setSaving(true);
                await Promise.resolve(onSaveToLibrary());
                setSaving(false);
              }}
            >
              {saving ? (
                <>
                  <Spinner animation='border' size='sm' /> Saving…
                </>
              ) : (
                <>⭐ Add to My Meals</>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Regular card ──────────────────────────────────
  let cardClass = 'mc-card';
  if (compact) cardClass += ' mc-card-compact';
  if (isShuffling) cardClass += ' mc-card-shuffling';
  if (isRevealing) cardClass += ' mc-card-reveal';
  if (isFlipped) cardClass += ' mc-card-flipped';

  return (
    <div className={cardClass}>
      <div className='mc-label'>
        <span className='mc-label-icon'>{typeIcon}</span>
        {typeLabel}
      </div>

      <div className='mc-flipper' onClick={onFlip}>
        {/* Front — image */}
        <div className='mc-front'>
          {!imgLoaded && <div className='mc-img-placeholder' />}
          <img
            className='mc-image'
            src={plan.mealImageUrl || PLACEHOLDER_IMG}
            alt={plan.mealName}
            loading='lazy'
            decoding='async'
            onLoad={() => setImgLoaded(true)}
          />
          <div className='mc-flip-hint'>
            <i className='fa-solid fa-list'></i> Ingredients
          </div>
        </div>

        {/* Back — ingredients */}
        <div className='mc-back'>
          <div className='mc-back-title'>
            <i className='fa-solid fa-basket-shopping'></i> Ingredients
          </div>
          <ul className='mc-ingredients'>
            {plan.mealIngredients && plan.mealIngredients.length > 0 ? (
              plan.mealIngredients.map((ing) => (
                <li key={ing.id}>{ing.name}</li>
              ))
            ) : (
              <li className='mc-no-ing'>No ingredients listed</li>
            )}
          </ul>
          <div className='mc-back-hint'>Tap to flip back</div>
        </div>
      </div>

      <div className='mc-body'>
        <div className='mc-name'>
          {plan.mealName}
          {plan.isOwnMeal && <span className='mc-own-badge'> My Meal</span>}
        </div>
        {plan.reason && <div className='mc-reason'>"{plan.reason}"</div>}

        <div className='mc-actions'>
          {plan.mealVideoUrl && onVideo && (
            <button
              className='mc-btn mc-btn-video'
              onClick={() => onVideo(plan.mealVideoUrl!)}
            >
              <i className='fa-solid fa-play'></i> Video
            </button>
          )}
          {onShuffle && (
            <button
              className='mc-btn mc-btn-shuffle'
              disabled={isShuffling}
              onClick={onShuffle}
            >
              {isShuffling ? (
                <Spinner animation='border' size='sm' />
              ) : (
                <>
                  <i className='fa-solid fa-shuffle'></i> Shuffle
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
